import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Activity, ChevronRight, Shield, TrendingUp, Bell, BarChart3, Sparkles, Target, Radio, RefreshCw, Gift, X, Zap, Brain, History, Users, AlertTriangle, MapPin } from 'lucide-react';
import { useSEO, SEO_KEYWORDS } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [accuracy, setAccuracy] = useState(null);
  const [liveGames, setLiveGames] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [showTrialBanner, setShowTrialBanner] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [trialDays, setTrialDays] = useState(15);

  // Fetch trial days
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pricing`);
        const data = await response.json();
        setTrialDays(data.trial_days || 15);
        setPricing(data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  // SEO optimization for landing page
  useSEO({
    title: 'Sports Predictions & Analysis | Who Will Win Tonight?',
    description: `Get AI-powered sports predictions with 60-80% accuracy. Find out who will win tonight's NHL, NBA, NFL, MLB games. Free ${trialDays}-day trial. Expert picks and win probability analysis.`,
    keywords: SEO_KEYWORDS.home,
    canonicalUrl: 'https://sportsanalysisab.ca/',
    ogImage: 'https://sportsanalysisab.ca/og-image.png'
  });

  // Handle View Today's Games click - require login
  const handleViewGames = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/board');
    }
  };

  useEffect(() => {
    // Fetch accuracy stats for homepage
    const fetchAccuracy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/accuracy`);
        if (response.ok) {
          const data = await response.json();
          setAccuracy(data);
        }
      } catch (err) {
        console.error('Failed to fetch accuracy:', err);
      }
    };
    fetchAccuracy();
  }, []);

  // Fetch trial info for banner
  useEffect(() => {
    const fetchTrialInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/trial-info`);
        if (response.ok) {
          const data = await response.json();
          setTrialInfo(data);
        }
      } catch (err) {
        console.error('Failed to fetch trial info:', err);
      }
    };
    fetchTrialInfo();
  }, []);

  // Fetch pricing for referral info
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pricing/public`);
        if (response.ok) {
          const data = await response.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  // Fetch live games function
  const fetchLiveGames = async (showRefreshState = false) => {
    if (showRefreshState) setIsRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/games/live`);
      if (response.ok) {
        const data = await response.json();
        // Sort live games by league
        const sortedGames = (data.live_games || []).sort((a, b) => {
          // Define league priority order
          const leagueOrder = {
            'nba': 1, 'nhl': 2, 'nfl': 3, 'mlb': 4, 'ncaab': 5,
            'mls': 6, 'epl': 7, 'argentina': 8, 'brazil': 9, 'concacaf': 10, 'cfl': 11
          };
          const orderA = leagueOrder[a.league_id?.toLowerCase()] || 99;
          const orderB = leagueOrder[b.league_id?.toLowerCase()] || 99;
          if (orderA !== orderB) return orderA - orderB;
          // Within same league, sort by start time
          return (a.start_time || '').localeCompare(b.start_time || '');
        });
        setLiveGames(sortedGames);
      }
    } catch (err) {
      console.error('Failed to fetch live games:', err);
    } finally {
      setLiveLoading(false);
      if (showRefreshState) setIsRefreshing(false);
    }
  };

  // Fetch live games and poll every 30 seconds
  useEffect(() => {
    fetchLiveGames();
    const interval = setInterval(() => fetchLiveGames(false), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    setShowAgeGate(true);
  };

  const confirmAge = () => {
    setShowAgeGate(false);
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 via-transparent to-transparent pointer-events-none" />
      <div className="noise-overlay absolute inset-0 pointer-events-none" />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-start">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-sm px-3 py-1.5 mb-8">
              <Activity className="w-4 h-4 text-red-400" />
              <span className="text-xs font-medium text-red-400 uppercase tracking-wider">
                Alberta Sports Analytics
              </span>
            </div>

            {/* Main headline */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight max-w-4xl mb-6">
              Data-Driven
              <span className="block text-red-500">Game Analysis</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mb-8">
              Professional-grade statistical previews and probability estimates for NHL, NBA, NFL, MLB, 
              CFL, and soccer. For entertainment and educational purposes.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="btn-blaze rounded-sm font-bold uppercase tracking-wider text-sm px-8 py-6"
                onClick={handleGetStarted}
                data-testid="get-started-btn"
              >
                Get Started
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="rounded-sm font-medium text-sm px-8 py-6"
                onClick={handleViewGames}
                data-testid="view-board-btn"
              >
                View Today's Games
              </Button>
            </div>

            {/* Trust badge */}
            <div className="flex items-center gap-2 mt-8 text-sm text-muted-foreground">
              <Shield className="w-4 h-4" />
              <span>18+ Only • For Entertainment Only • Not Betting Advice</span>
            </div>
          </div>
        </div>
      </section>

      {/* Live Games Scoreboard - Always visible */}
      <section className="py-6 px-4 bg-red-500/5 border-y border-red-500/20" data-testid="live-scoreboard">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Radio className={`w-5 h-5 text-red-400 ${liveGames.length > 0 ? 'animate-pulse' : ''}`} />
              <h2 className="font-display text-lg font-bold uppercase tracking-tight text-red-400">
                Live Scores
              </h2>
              {liveGames.length > 0 && (
                <Badge variant="outline" className="text-red-400 border-red-400/30">
                  {liveGames.length} {liveGames.length === 1 ? 'Game' : 'Games'}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => fetchLiveGames(true)}
                disabled={isRefreshing}
                data-testid="refresh-live-scores-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <span className="text-xs text-muted-foreground">Auto-updates every 30s</span>
            </div>
          </div>

          {liveLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-muted-foreground animate-spin" />
              <span className="ml-2 text-muted-foreground">Loading live scores...</span>
            </div>
          ) : liveGames.length > 0 ? (
            <div className="space-y-4">
              {/* Group games by league */}
              {Object.entries(
                liveGames.reduce((groups, game) => {
                  const league = game.league_name || game.league_id?.toUpperCase() || 'OTHER';
                  if (!groups[league]) groups[league] = [];
                  groups[league].push(game);
                  return groups;
                }, {})
              ).map(([league, games]) => (
                <div key={league}>
                  <h3 className="text-sm font-bold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Activity className="w-3 h-3" />
                    {league} <span className="text-muted-foreground font-normal">({games.length} live)</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {games.map((game) => (
                <Link
                  key={game.game_id}
                  to={`/game/${game.game_id}`}
                  className="bg-card border border-red-500/30 rounded-sm p-3 hover:border-red-500/50 transition-colors"
                  data-testid={`live-game-${game.game_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs uppercase font-mono">
                      {game.league_name}
                    </Badge>
                    <span className="text-xs text-red-400">{game.status_detail || 'LIVE'}</span>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Away Team */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-secondary rounded-sm flex items-center justify-center overflow-hidden">
                          {game.away_team?.logo_url ? (
                            <img 
                              src={game.away_team.logo_url} 
                              alt={game.away_team.name}
                              className="w-5 h-5 object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="text-xs font-bold">{game.away_team?.abbreviation}</span>
                          )}
                        </div>
                        <span className="text-sm font-medium truncate max-w-[100px]">{game.away_team?.name}</span>
                      </div>
                      <span className={`font-mono text-lg font-bold ${game.away_score > game.home_score ? 'text-green-400' : ''}`}>
                        {game.away_score}
                      </span>
                    </div>

                    {/* Home Team */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-secondary rounded-sm flex items-center justify-center overflow-hidden">
                          {game.home_team?.logo_url ? (
                            <img 
                              src={game.home_team.logo_url} 
                              alt={game.home_team.name}
                              className="w-5 h-5 object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="text-xs font-bold">{game.home_team?.abbreviation}</span>
                          )}
                        </div>
                        <span className="text-sm font-medium truncate max-w-[100px]">{game.home_team?.name}</span>
                      </div>
                      <span className={`font-mono text-lg font-bold ${game.home_score > game.away_score ? 'text-green-400' : ''}`}>
                        {game.home_score}
                      </span>
                    </div>
                  </div>
                </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-card/50 rounded-sm border border-border">
              <Radio className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">No games are live right now</p>
              <p className="text-sm text-muted-foreground mt-1">Check back during game times for live scores</p>
              <Button 
                variant="outline" 
                className="mt-4 rounded-sm"
                onClick={handleViewGames}
              >
                View Upcoming Games
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Accuracy Stats Section */}
      {accuracy && accuracy.total_predictions > 0 && (
        <section 
          className="py-12 px-4 bg-gradient-to-r from-green-500/10 via-transparent to-green-500/10 border-y border-green-500/20"
          data-testid="platform-accuracy-section"
        >
          <div className="max-w-6xl mx-auto">
            {/* Main Stats Row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Target className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wider">Platform Accuracy</p>
                  <p className="font-display text-4xl font-bold text-green-400">
                    {accuracy.accuracy_percentage}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Powered by AI Continuous Learning
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-8 text-center">
                <div>
                  <p className="font-display text-2xl font-bold">{accuracy.total_predictions}</p>
                  <p className="text-xs text-muted-foreground uppercase">Predictions</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-bold text-green-400">{accuracy.correct_predictions}</p>
                  <p className="text-xs text-muted-foreground uppercase">Correct</p>
                </div>
                {accuracy.hot_streak > 0 && (
                  <div>
                    <p className="font-display text-2xl font-bold text-orange-400">{accuracy.hot_streak}</p>
                    <p className="text-xs text-muted-foreground uppercase">Hot Streak</p>
                  </div>
                )}
              </div>

              <Button 
                variant="outline"
                className="rounded-sm border-green-500/30 text-green-400 hover:bg-green-500/10"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/billing');
                }}
                data-testid="view-accuracy-cta"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Subscribe for Detailed Reports
              </Button>
            </div>

            {/* AI Learning Info Section */}
            <div className="bg-card/50 border border-border/50 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Brain className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight">AI Continuous Learning System</h3>
                  <p className="text-sm text-muted-foreground">Our AI improves with every game, learning what factors lead to accurate predictions</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm">
                  <History className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium">Past Games</p>
                    <p className="text-[10px] text-muted-foreground">Historical outcomes</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm">
                  <Users className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium">Player Stats</p>
                    <p className="text-[10px] text-muted-foreground">Performance trends</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm">
                  <TrendingUp className="w-4 h-4 text-orange-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium">Team Form</p>
                    <p className="text-[10px] text-muted-foreground">Recent momentum</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm">
                  <Activity className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium">Head-to-Head</p>
                    <p className="text-[10px] text-muted-foreground">Matchup history</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium">Injuries</p>
                    <p className="text-[10px] text-muted-foreground">Player availability</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-sm">
                  <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium">Home/Away</p>
                    <p className="text-[10px] text-muted-foreground">Venue advantage</p>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground text-center mt-4 pt-4 border-t border-border/30">
                After each game, our AI analyzes what factors led to the outcome and adjusts future predictions accordingly
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Pro Analysis Promo - DEACTIVATED */}
      {false && (
      <section className="py-16 px-4 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/30 rounded-sm p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/20 rounded-sm flex items-center justify-center">
                  <BarChart3 className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">
                    Pro Analysis
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Get expert predictions for any team, player, or matchup from our analytics engine.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-bold font-mono">$0.99</p>
                  <p className="text-xs text-muted-foreground">per analysis</p>
                </div>
                <Button 
                  className="btn-blaze rounded-sm"
                  onClick={() => navigate('/analysis')}
                  data-testid="pro-analysis-cta"
                >
                  Try Pro Analysis
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Features Section */}
      <section className="py-20 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-4 text-center">
            Why SportsAnalysisAB?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Data-driven sports analysis that gives you the edge. Our AI-powered predictions combine statistical models with real-time data to help you understand game outcomes.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="w-12 h-12 bg-red-500/10 rounded-sm flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-2">
                AI-Powered Analysis
              </h3>
              <p className="text-sm text-muted-foreground">
                Our proprietary AI analyzes 50+ factors per game: historical data, recent form, head-to-head records, 
                injuries, rest days, and venue advantages.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-2">
                Transparent Accuracy
              </h3>
              <p className="text-sm text-muted-foreground">
                Track our prediction accuracy in real-time. We show you our win rate by league, confidence level, 
                and daily performance. No hidden stats.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center mb-4">
                <Bell className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-2">
                Smart Alerts
              </h3>
              <p className="text-sm text-muted-foreground">
                Get notified about high-confidence matchups. Customize alerts for your favorite teams and leagues.
                Never miss a good opportunity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Why We're Different */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-4 text-center">
            How We Compare
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Unlike other sports prediction platforms, we focus on transparency, data-driven analysis, and continuous learning.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 font-display uppercase text-sm tracking-wider">Feature</th>
                  <th className="text-center py-4 px-4 font-display uppercase text-sm tracking-wider text-green-400">SportsAnalysisAB</th>
                  <th className="text-center py-4 px-4 font-display uppercase text-sm tracking-wider text-muted-foreground">Other Platforms</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">AI-Powered Predictions</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ GPT-5 Powered</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Basic algorithms</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">Real-Time Accuracy Tracking</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ Live Dashboard</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Hidden or None</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">Score Predictions</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ AI Score Forecast</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Win/Lose Only</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">Injury Impact Analysis</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ Real-time Updates</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Manual updates</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">Post-Game Learning</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ Auto-Improves</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Static models</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">Live Scores</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ 30-Second Updates</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Delayed or None</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-sm">Free Trial</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ 7 Days Full Access</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">Limited or Paid</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-sm">Canadian Focus</td>
                  <td className="py-4 px-4 text-center text-green-400 font-bold">✓ Alberta-Based</td>
                  <td className="py-4 px-4 text-center text-muted-foreground">US-Centric</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Built for Canadian sports fans who want data-driven insights, not gambling advice.
            </p>
            <Button 
              className="btn-blaze rounded-sm font-bold uppercase tracking-wider text-sm px-8 py-4"
              onClick={handleGetStarted}
            >
              Start Your Free Trial
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Leagues Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-4">
            All Major Leagues
          </h2>
          <p className="text-muted-foreground mb-12">
            Coverage across North American and international sports
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            {['NHL', 'NBA', 'NFL', 'MLB', 'CFL', 'EPL', 'MLS'].map((league) => (
              <div 
                key={league}
                className="bg-card border border-border rounded-sm px-6 py-3 font-display font-bold uppercase tracking-wider"
              >
                {league}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
      {pricing && (
        <section className="py-20 px-4 bg-gradient-to-b from-green-500/5 to-emerald-500/10" data-testid="referral-section">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-4">
                <Gift className="w-3 h-3 mr-1" />
                Referral Program
              </Badge>
              <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-4">
                Earn While You Share
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Love SportsAnalysisAB? Share it with your friends and both of you benefit!
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* You Get */}
              <div className="bg-card border border-green-500/20 rounded-lg p-6 text-center hover:border-green-500/40 transition-colors">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold mb-2">You Earn</h3>
                <p className="text-4xl font-bold text-green-400 mb-2">
                  ${(pricing.subscription_price * pricing.referrer_credit_percent / 100).toFixed(0)} Credit
                </p>
                <p className="text-muted-foreground text-sm">
                  {pricing.referrer_credit_percent}% of subscription price for each friend who signs up
                </p>
              </div>
              
              {/* They Get */}
              <div className="bg-card border border-emerald-500/20 rounded-lg p-6 text-center hover:border-emerald-500/40 transition-colors">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold mb-2">They Get</h3>
                <p className="text-4xl font-bold text-emerald-400 mb-2">
                  ${(pricing.subscription_price * pricing.referee_credit_percent / 100).toFixed(0)} Off
                </p>
                <p className="text-muted-foreground text-sm">
                  {pricing.referee_credit_percent}% discount on their first month
                </p>
              </div>
            </div>
            
            {/* Trial Days Info */}
            {pricing.trial_days > 0 && (
              <div className="mt-8 text-center">
                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-6 py-3">
                  <Zap className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-semibold">
                    Plus {pricing.trial_days} Days Free Trial for Everyone!
                  </span>
                </div>
              </div>
            )}
            
            <div className="mt-8 text-center">
              <Button 
                className="btn-blaze rounded-sm font-bold uppercase tracking-wider"
                onClick={() => navigate('/signup')}
                data-testid="referral-signup-btn"
              >
                <Gift className="w-4 h-4 mr-2" />
                Start Earning Today
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-t from-red-500/10 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-4">
            Ready to Analyze?
          </h2>
          <p className="text-muted-foreground mb-8">
            Join thousands of sports fans using data-driven insights for their game day experience.
          </p>
          <Button 
            size="lg"
            className="btn-blaze rounded-sm font-bold uppercase tracking-wider text-sm px-8 py-6"
            onClick={handleGetStarted}
            data-testid="cta-get-started-btn"
          >
            Create Free Account
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Age Gate Modal */}
      {showAgeGate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-sm max-w-md w-full p-8">
            <div className="text-center">
              <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight mb-4">
                Age Verification
              </h3>
              <p className="text-muted-foreground mb-6">
                You must be 18 years or older to use SportsAnalysisAB. 
                By continuing, you confirm that you are of legal age.
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider"
                  onClick={confirmAge}
                  data-testid="confirm-age-btn"
                >
                  I am 18 or older
                </Button>
                <Button 
                  variant="outline"
                  className="w-full rounded-sm"
                  onClick={() => setShowAgeGate(false)}
                  data-testid="cancel-age-btn"
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                This site is for entertainment purposes only. Not betting advice.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Floating Free Trial Banner */}
      {!isAuthenticated && showTrialBanner && trialInfo && (
        <div 
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-3rem)] max-w-xl animate-in slide-in-from-bottom-5 duration-500"
          data-testid="free-trial-banner"
        >
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-red-500 p-[2px] rounded-lg shadow-2xl">
            <div className="bg-card rounded-lg p-4 relative">
              <button 
                onClick={() => setShowTrialBanner(false)}
                className="absolute top-2 right-2 p-1 hover:bg-secondary rounded-sm transition-colors"
                data-testid="close-trial-banner"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                  <Gift className="w-7 h-7 text-white" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-xs font-bold text-yellow-400 uppercase tracking-wider">
                      Limited Time Offer
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                    {trialInfo.trial_days}-Day FREE Trial
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Full access to predictions • No credit card required to start
                  </p>
                </div>
                
                <Button 
                  className="btn-blaze rounded-sm font-bold text-sm px-5 whitespace-nowrap flex-shrink-0"
                  onClick={handleGetStarted}
                  data-testid="trial-banner-cta"
                >
                  Start Free
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-center gap-6 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3 text-green-400" />
                  AI Predictions
                </span>
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3 text-blue-400" />
                  Live Scores
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3 text-purple-400" />
                  Player Stats
                </span>
                <span className="flex items-center gap-1">
                  <Bell className="w-3 h-3 text-yellow-400" />
                  Smart Alerts
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
